import * as yup from "yup";
import FormConfig from "../../Config/FormConfig.js";

// Step 1 mirrors the Edit Account Modal minus its Account Setup tab, minus
// AccountNumber / BillingAccountID / the BAA upload (plan D13).
export const ACCOUNT_ENROLLMENT_SECTIONS = [
  { key: "account", title: "Account" },
  { key: "shipping", title: "Shipping" },
  { key: "billing", title: "Billing" },
  {
    key: "provider",
    title: "Provider Information",
  },
  {
    key: "dpa",
    title: "DPA Contact Information",
    helperText: "The DPA will be sent to the contact below",
  },
  { key: "identifiers", title: "Identifiers" },
];

export const BILLING_SAME_AS_SHIPPING_KEY = "BillingSameAsShipping";

// Account Contacts: Tissue Vault users requested with the enrollment. Created
// on the new parent account at approval, but not invited.
export const ACCOUNT_CONTACTS_KEY = "AccountContacts";
export const ACCOUNT_CONTACTS_SECTION = {
  key: "contacts",
  title: "Account Contacts",
  helperText:
    "Request Tissue Vault access for the following users. These users will be added to this primary location (Parent Account) only. By default, users in the primary location have all child locations in their scope. To request users with only specific locations in their scope, please contact Advanced Solution after account creation.",
};
export const MAX_ACCOUNT_CONTACTS = 50;

export const ACCOUNT_CONTACT_FIELDS = [
  { key: "Salutation", label: "Salutation", placeholder: "Dr." },
  { key: "FirstName", label: "First Name", required: true },
  { key: "LastName", label: "Last Name", required: true },
  { key: "Email", label: "Email", required: true },
  { key: "Phone", label: "Phone" },
];

export const createEmptyAccountContact = () => ({
  Salutation: "",
  FirstName: "",
  LastName: "",
  Email: "",
  Phone: "",
  IsProvider: false,
  ProviderNPI: "",
});

// Billing field <- shipping field. Drives the "same as shipping" checkbox:
// while it is checked these targets are copied from their source and locked.
export const BILLING_SHIPPING_MIRROR = {
  BillingAddressLine1: "AddressLine1",
  BillingAddressLine2: "AddressLine2",
  BillingCity: "City",
  BillingState: "State",
  BillingZip: "Zip",
};

export const ACCOUNT_ENROLLMENT_FIELDS = [
  { key: "AccountName", label: "Account Name", section: "account", required: true },
  { key: "DistributorID", label: "Distributor", section: "account", required: true, type: "select" },

  // State drives Region / MAC / DME MAC, so it sits with the address it
  // belongs to and Region follows it directly.
  { key: "AddressLine1", label: "Address Line 1", section: "shipping", required: true },
  { key: "AddressLine2", label: "Address Line 2", section: "shipping" },
  { key: "City", label: "City", section: "shipping", required: true },
  { key: "State", label: "State", section: "shipping", required: true, type: "select" },
  { key: "Zip", label: "ZIP", section: "shipping", required: true },
  { key: "Phone", label: "Phone", section: "shipping" },
  { key: "Fax", label: "Fax", section: "shipping" },
  { key: "RegionID", label: "Region", section: "shipping", required: true, type: "select" },

  { key: "APContact", label: "AP Contact Name", section: "billing" },
  { key: "BillingAddressLine1", label: "Billing Address Line 1", section: "billing" },
  { key: "BillingAddressLine2", label: "Billing Address Line 2", section: "billing" },
  { key: "BillingCity", label: "Billing City", section: "billing" },
  { key: "BillingState", label: "Billing State", section: "billing", type: "select" },
  { key: "BillingZip", label: "Billing ZIP", section: "billing" },

  { key: "ProviderName", label: "Provider Name", section: "provider" },
  { key: "ProviderNPI", label: "Provider NPI", section: "provider" },
  { key: "ProviderEmail", label: "Provider Email", section: "provider", validator: "email" },
  { key: "ProviderPhone", label: "Provider Phone", section: "provider" },

  { key: "DPAContactName", label: "DPA Contact Name", section: "dpa", required: true },
  { key: "DPAContactEmail", label: "DPA Contact Email", section: "dpa", required: true, validator: "email" },
  { key: "DPAContactPhone", label: "DPA Contact Phone", section: "dpa" },

  { key: "MedicareAdminContractor", label: "Medicare Admin Contractor", section: "identifiers" },
  { key: "DMEMAC", label: "DME MAC", section: "identifiers" },
  { key: "NPI", label: "NPI", section: "identifiers", required: true },
  { key: "PTAN", label: "PTAN", section: "identifiers", required: true },
  { key: "DMEPTAN", label: "DME PTAN", section: "identifiers" },
  { key: "EIN", label: "EIN", section: "identifiers", required: true },
];

export const ACCOUNT_ENROLLMENT_FIELD_LABELS = Object.fromEntries(
  ACCOUNT_ENROLLMENT_FIELDS.map((field) => [field.key, field.label]),
);

export const ACCOUNT_ENROLLMENT_REQUIRED_FIELDS = ACCOUNT_ENROLLMENT_FIELDS.filter(
  (field) => field.required,
).map((field) => field.key);

export const getEnrollmentFieldsForSection = (sectionKey) =>
  ACCOUNT_ENROLLMENT_FIELDS.filter((field) => field.section === sectionKey);

/** Copy the shipping address over the billing fields the checkbox governs. */
export const applyBillingSameAsShipping = (values = {}) => {
  const next = { ...values };
  Object.entries(BILLING_SHIPPING_MIRROR).forEach(([target, source]) => {
    next[target] = values[source] ?? (target === "BillingState" ? null : "");
  });
  return next;
};

/** True when every mirrored billing field already matches its shipping source. */
export const billingMatchesShipping = (values = {}) =>
  Object.entries(BILLING_SHIPPING_MIRROR).every(
    ([target, source]) =>
      normalizeEnrollmentValue(values[target]) ===
      normalizeEnrollmentValue(values[source]),
  );

// Reuse the shared account validators exactly the way accountChangeRequestFields
// does, so enrollment and the edit modal cannot drift apart.
const existingValidators = new Map(
  FormConfig.CreateAccountForm.filter((field) => field.validationFunction).map(
    (field) => [field.backendName, field.validationFunction],
  ),
);

const optionalTextValidator = yup
  .string()
  .nullable()
  .max(255, "Must be 255 characters or fewer");

const emailValidator = yup
  .string()
  .nullable()
  .email("Enter a valid email address")
  .max(255, "Must be 255 characters or fewer");

const namedValidators = { email: emailValidator };

const selectValidator = (field) =>
  field.required
    ? yup
        .mixed()
        .test(
          "required-selection",
          `${field.label} is required`,
          (value) => value !== null && value !== undefined && value !== "",
        )
    : yup.mixed().nullable();

const accountEnrollmentValidationFields = ACCOUNT_ENROLLMENT_FIELDS.reduce(
  (schema, field) => {
    if (field.type === "select") {
      schema[field.key] = selectValidator(field);
      return schema;
    }
    // The shared validators only enforce length for most fields (the edit
    // form checks "required" separately), so layer the required rule on top
    // for the wizard's own required set.
    const sharedValidator =
      namedValidators[field.validator] ||
      existingValidators.get(field.key) ||
      optionalTextValidator;
    schema[field.key] = field.required
      ? sharedValidator.required(`${field.label} is required`)
      : sharedValidator;
    return schema;
  },
  {},
);

const contactTextValidator = (label, { required = false, max = 255 } = {}) => {
  const validator = yup
    .string()
    .nullable()
    .trim()
    .max(max, `Must be ${max} characters or fewer`);
  return required ? validator.required(`${label} is required`) : validator;
};

const normalizeContactEmail = (value) => String(value ?? "").trim().toLowerCase();

const accountContactSchema = yup.object().shape({
  Salutation: contactTextValidator("Salutation"),
  FirstName: contactTextValidator("First Name", { required: true }),
  LastName: contactTextValidator("Last Name", { required: true }),
  Email: contactTextValidator("Email", { required: true })
    .email("Enter a valid email address")
    .test("unique-email", "This email is already listed", function (value) {
      const email = normalizeContactEmail(value);
      if (!email) return true;
      const contacts = this.from?.[1]?.value?.[ACCOUNT_CONTACTS_KEY] || [];
      return contacts.filter((contact) => normalizeContactEmail(contact?.Email) === email).length < 2;
    }),
  Phone: contactTextValidator("Phone", { max: 50 }),
  IsProvider: yup.boolean(),
  ProviderNPI: yup
    .string()
    .nullable()
    .when("IsProvider", {
      is: true,
      then: (schema) =>
        schema
          .trim()
          .required("Provider NPI is required")
          .matches(/^\d{10}$/, "Provider NPI must be exactly 10 digits"),
      otherwise: (schema) => schema,
    }),
});

export const accountEnrollmentValidationSchema = yup.object().shape({
  ...accountEnrollmentValidationFields,
  [ACCOUNT_CONTACTS_KEY]: yup
    .array()
    .of(accountContactSchema)
    .max(MAX_ACCOUNT_CONTACTS, `No more than ${MAX_ACCOUNT_CONTACTS} account contacts can be requested`),
});

export const normalizeEnrollmentValue = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const buildAccountEnrollmentInitialValues = (overrides = {}) => {
  const values = ACCOUNT_ENROLLMENT_FIELDS.reduce((result, field) => {
    result[field.key] = field.type === "select" ? null : "";
    return result;
  }, {});
  // UI-only: governs the billing copy, never part of the payload.
  values[BILLING_SAME_AS_SHIPPING_KEY] = false;
  // Sent beside Account, not inside it.
  values[ACCOUNT_CONTACTS_KEY] = [];
  return { ...values, ...overrides };
};

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const isAccountDetailsStepComplete = (values = {}, errors = {}) =>
  ACCOUNT_ENROLLMENT_REQUIRED_FIELDS.every(
    (key) => hasValue(values[key]) && !errors[key],
  ) && !errors[ACCOUNT_CONTACTS_KEY];

export const buildAccountDetailsPayload = (values = {}) =>
  ACCOUNT_ENROLLMENT_FIELDS.reduce((payload, field) => {
    payload[field.key] =
      field.type === "select"
        ? (values[field.key] ?? null)
        : normalizeEnrollmentValue(values[field.key]);
    return payload;
  }, {});

/** Contacts as the backend expects them: trimmed, blanks → null, NPI only for providers. */
export const buildAccountContactsPayload = (contacts = []) =>
  (contacts || []).map((contact) => {
    const isProvider = Boolean(contact?.IsProvider);
    return {
      Salutation: normalizeEnrollmentValue(contact?.Salutation),
      FirstName: normalizeEnrollmentValue(contact?.FirstName),
      LastName: normalizeEnrollmentValue(contact?.LastName),
      Email: normalizeEnrollmentValue(contact?.Email)?.toLowerCase() ?? null,
      Phone: normalizeEnrollmentValue(contact?.Phone),
      IsProvider: isProvider,
      ProviderNPI: isProvider ? normalizeEnrollmentValue(contact?.ProviderNPI) : null,
    };
  });

export const formatAccountContactName = (contact = {}) =>
  [contact?.Salutation, contact?.FirstName, contact?.LastName]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
